import { useCallback, useEffect, useMemo, useState } from "react";
import { SAMPLE_SYSTEM_ANALYSIS } from "@/config/systemAnalysisSample.data";
import { EN_TRANSLATIONS, NO_TRANSLATIONS } from "@/config/translations.data";
import {
  fetchDeployedWorkbook,
  parseWorkbook,
  resolveDataFile,
  type ParsedWorkbook,
} from "@/data/excel";
import { fetchInterventions, importInterventionRows } from "@/data/interventionsRepository";
import { fetchCustomFields, type CustomField } from "@/data/customFieldsRepository";
import { fetchMatrixSettings, type MatrixSettings } from "@/data/settingsRepository";
import {
  fetchSystemAnalyses,
  importSystemAnalysis,
  mapSystemAnalysisRows,
} from "@/data/systemAnalysisRepository";
import { DEFAULT_AGE_GROUPS } from "@/domain/columnLayouts";
import { DEFAULT_URL_PARAMS, type DefaultUrlParams } from "@/domain/defaultUrlParams";
import { DEFAULT_LANGUAGES, type Dictionary, type Language } from "@/domain/dictionary";
import {
  DEFAULT_EXPERIMENTAL_FEATURES,
  type ExperimentalFeatures,
} from "@/domain/experimentalFeatures";
import { parseInterventions, type InterventionRecord } from "@/domain/interventions";
import { isRespondentOnly, type AuthMode, type CurrentUser } from "@/domain/permissions";
import {
  buildSystemAnalyses,
  createPlaceholderDataset,
  type SystemAnalysisDataset,
} from "@/domain/systemAnalysis";
import type { AgeGroup, Arena, InterventionDataset, View } from "@/domain/types";
import { DEFAULT_VIEWS } from "@/domain/views";

/** Settings in effect: database settings in individual mode, built-in defaults otherwise. */
export interface EffectiveSettings {
  views: View[];
  customFields: CustomField[];
  ageGroups: AgeGroup[];
  /** `null` in shared mode (rows come from the workbook's Arena column). */
  arenas: Arena[] | null;
  languages: Language[];
  /** `null` in shared mode (built-in texts only). */
  dictionary: Dictionary | null;
  /** All off in shared mode. */
  experimentalFeatures: ExperimentalFeatures;
  defaultUrlParams: DefaultUrlParams;
}

const SHARED_SETTINGS: EffectiveSettings = {
  views: DEFAULT_VIEWS,
  customFields: [],
  ageGroups: DEFAULT_AGE_GROUPS,
  arenas: null,
  languages: DEFAULT_LANGUAGES,
  dictionary: null,
  experimentalFeatures: DEFAULT_EXPERIMENTAL_FEATURES,
  defaultUrlParams: DEFAULT_URL_PARAMS,
};

const PLACEHOLDER_SYSTEM = createPlaceholderDataset(SAMPLE_SYSTEM_ANALYSIS, {
  no: NO_TRANSLATIONS.systemInsufficientInformation,
  en: EN_TRANSLATIONS.systemInsufficientInformation,
});

export type LoadStatus =
  { state: "idle" } | { state: "loading" } | { state: "ready" } | { state: "error"; error: Error };

/** Raised by `importLocalWorkbook`; tells whether the interventions were saved before the failure. */
export class ImportError extends Error {
  constructor(
    readonly original: unknown,
    readonly interventionsImported: boolean,
  ) {
    super(original instanceof Error ? original.message : String(original));
  }
}

export interface LocalWorkbook {
  fileName: string;
  workbook: ParsedWorkbook;
}

export interface AppData {
  status: LoadStatus;
  settings: EffectiveSettings;
  dataset: InterventionDataset;
  systemData: SystemAnalysisDataset;
  /** Set when loading system analyses from the database failed. */
  systemError: Error | null;
  /** Workbook base name (shared mode or local file), used for RePS-specific rules. */
  dataFile: string | null;
  /** A workbook the superadmin opened locally, ready to import. */
  localWorkbook: LocalWorkbook | null;
  /** Reloads everything from the source. Resolves to `false` when loading failed. */
  reload(): Promise<boolean>;
  /** Shows a local workbook without saving it anywhere. */
  openLocalWorkbook(file: File): Promise<void>;
  /** Superadmin: writes the open local workbook to the database. */
  importLocalWorkbook(): Promise<{
    interventions: number;
    system: { criteria: number; assessments: number } | null;
  }>;
}

function toSettings(settings: MatrixSettings, customFields: CustomField[]): EffectiveSettings {
  return { ...settings, customFields };
}

function systemFromWorkbook(
  workbook: ParsedWorkbook,
  fallback: SystemAnalysisDataset,
): SystemAnalysisDataset {
  if (!workbook.systemAnalysis) return fallback;
  const parsed = buildSystemAnalyses(
    workbook.systemAnalysis.criterionRows,
    workbook.systemAnalysis.dataRows,
  );
  return parsed.analyses.length ? parsed : fallback;
}

interface LoadedData {
  settings: EffectiveSettings;
  records: InterventionRecord[];
  systemData: SystemAnalysisDataset;
  systemError: Error | null;
  dataFile: string | null;
}

const toError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

/** Shared mode: the deployed workbook with built-in settings. A missing workbook just means "empty". */
async function loadShared(): Promise<LoadedData> {
  const dataFile = resolveDataFile(new URLSearchParams(window.location.search).get("data"));
  const workbook = await fetchDeployedWorkbook(dataFile);
  return {
    settings: SHARED_SETTINGS,
    records: workbook ? workbook.interventionRows.map((row) => ({ row })) : [],
    systemData: workbook ? systemFromWorkbook(workbook, PLACEHOLDER_SYSTEM) : PLACEHOLDER_SYSTEM,
    systemError: null,
    dataFile,
  };
}

/** Individual mode: settings, interventions and system analyses from Supabase (RLS-filtered). */
async function loadIndividual(user: CurrentUser): Promise<LoadedData> {
  const [matrixSettings, records, customFields] = await Promise.all([
    fetchMatrixSettings(),
    fetchInterventions(),
    fetchCustomFields(),
  ]);
  const base = { settings: toSettings(matrixSettings, customFields), records, dataFile: null };
  if (isRespondentOnly(user)) return { ...base, systemData: { analyses: [] }, systemError: null };
  try {
    return { ...base, systemData: await fetchSystemAnalyses(), systemError: null };
  } catch (error) {
    // The rest of the app stays usable when only the system analysis fails.
    console.error("Could not load system analyses:", error);
    return { ...base, systemData: { analyses: [] }, systemError: toError(error) };
  }
}

function load(authMode: AuthMode, user: CurrentUser | null): Promise<LoadedData> | null {
  if (authMode === "shared") return loadShared();
  return user ? loadIndividual(user) : null;
}

export function useAppData(authMode: AuthMode | null, user: CurrentUser | null): AppData {
  const [status, setStatus] = useState<LoadStatus>({ state: "loading" });
  const [settings, setSettings] = useState<EffectiveSettings>(SHARED_SETTINGS);
  const [records, setRecords] = useState<InterventionRecord[]>([]);
  const [systemData, setSystemData] = useState<SystemAnalysisDataset>(PLACEHOLDER_SYSTEM);
  const [systemError, setSystemError] = useState<Error | null>(null);
  const [dataFile, setDataFile] = useState<string | null>(null);
  const [localWorkbook, setLocalWorkbook] = useState<LocalWorkbook | null>(null);

  const apply = useCallback((loaded: LoadedData) => {
    setSettings(loaded.settings);
    setRecords(loaded.records);
    setSystemData(loaded.systemData);
    setSystemError(loaded.systemError);
    setDataFile(loaded.dataFile);
    setStatus({ state: "ready" });
  }, []);

  const fail = useCallback((error: unknown) => {
    console.error("Could not load data:", error);
    setStatus({ state: "error", error: toError(error) });
  }, []);

  useEffect(() => {
    const pending = authMode ? load(authMode, user) : null;
    if (!pending) return;
    let current = true;
    pending.then(
      (loaded) => current && apply(loaded),
      (error: unknown) => current && fail(error),
    );
    return () => {
      current = false;
    };
  }, [authMode, user, apply, fail]);

  const reload = useCallback(async (): Promise<boolean> => {
    const pending = authMode ? load(authMode, user) : null;
    if (!pending) return false;
    try {
      apply(await pending);
      return true;
    } catch (error) {
      fail(error);
      return false;
    }
  }, [authMode, user, apply, fail]);

  const openLocalWorkbook = useCallback(async (file: File) => {
    const workbook = await parseWorkbook(await file.arrayBuffer());
    setLocalWorkbook({ fileName: file.name, workbook });
    setDataFile(file.name.replace(/\.[^.]*$/, ""));
    setRecords(workbook.interventionRows.map((row) => ({ row })));
    setSystemData((current) => systemFromWorkbook(workbook, current));
  }, []);

  const importLocalWorkbook = useCallback(async () => {
    if (!localWorkbook) throw new Error("No local workbook is open");
    const { interventionRows, systemAnalysis } = localWorkbook.workbook;
    let interventions = 0;
    let system: { criteria: number; assessments: number } | null = null;
    try {
      interventions = await importInterventionRows(interventionRows);
      if (systemAnalysis) {
        const rows = mapSystemAnalysisRows(systemAnalysis.criterionRows, systemAnalysis.dataRows);
        await importSystemAnalysis(rows);
        system = { criteria: rows.criterion_rows.length, assessments: rows.data_rows.length };
      }
    } catch (error) {
      throw new ImportError(error, interventions > 0);
    }
    setLocalWorkbook(null);
    await reload();
    return { interventions, system };
  }, [localWorkbook, reload]);

  const dataset = useMemo(
    () => parseInterventions(records, { views: settings.views, ageGroups: settings.ageGroups }),
    [records, settings.views, settings.ageGroups],
  );

  return {
    status,
    settings,
    dataset,
    systemData,
    systemError,
    dataFile,
    localWorkbook,
    reload,
    openLocalWorkbook,
    importLocalWorkbook,
  };
}
